import debounce from "../debounce";
import {validateField} from "./validation";
import {getEventTarget} from "../event";
import {getParentNodeWithClass} from "../helpers";

/**
 * Default options for autocompletes
 *
 * @type {{inputWrapperSelector: string, dataListSelector: string, validate: boolean}}
 */
const defaultOptions = {
  validate: true,
  inputWrapperSelector: "fe__input",
  dataListSelector: ".fe__datalist",
};

/**
 * Template for autocomplete option
 *
 * @type {string}
 */
const templateAutocompleteOptions = "\
  <div class=\"fe__datalist_option_wrapper\" data-value=\"{%value%}\"> \
    <div class=\"fe__datalist_option\">{%name%}</div> \
  </div>";

/**
 * Init autocomplete fields in form
 * Use:
 *    data-autocomplete-validate
 *    data-autocomplete-input-wrapper-selector
 *    data-autocomplete-data-list-selector
 * To configure options for each field take precedence:
 *    1) data- configuration options in field
 *    2) options passed to this function
 *    3) default options
 *
 * Options, can supply "list" attribute with the field id and callback function to get the list from,
 * should return a promise with an array of objects with name (What to show in list, can be HTML),
 * inField (What to show after selected in field), and value (What to mark as dataset.value in field)
 * {"list": {"my-field-id": function(value, field) Promise => Array}}
 *
 * @param form {HTMLFormElement}
 * @param options {Object}
 */
const initAutocompleteFieldsInForm = (form, options) => {
  form.querySelectorAll(".fe__autocomplete_input")
    .forEach(autocompleteField => {
      const fieldOpts = Object.assign({}, options);
      if (fieldOpts.list && "undefined" !== typeof (fieldOpts.list[autocompleteField.id])) {
        // Reset list to just the callback
        fieldOpts.list = fieldOpts.list[autocompleteField.id];
      }
      if (fieldOpts.staticList && "undefined" !== typeof (fieldOpts.staticList[autocompleteField.id])) {
        // Reset list to just the callback
        fieldOpts.staticList = fieldOpts.staticList[autocompleteField.id];
      }
      initAutocompleteField(autocompleteField, fieldOpts);
    });
};

/**
 * Init autocomplete field
 * Use:
 *    data-autocomplete-validate
 *    data-autocomplete-input-wrapper-selector
 *    data-autocomplete-data-list-selector
 * To configure options for each field take precedence:
 *    1) data- configuration options in field
 *    2) options passed to this function
 *    3) default options
 *
 * Options, can supply "list" attribute with a callback function to get the list from,
 * should return a promise with an array of objects with name (What to show in list, can be HTML),
 * inField (What to show after selected in field), and value (What to mark as dataset.value in field)
 * {"list": function(value, field) => Promise => Array}
 *
 * @param field {HTMLInputElement}
 * @param options {Object}
 */
const initAutocompleteField = (field, options) => {
  if (field.dataset.autocompleteInitialized) {
    return;
  }
  const opts = Object.assign(defaultOptions, options);
  if ("undefined" !== typeof (field.dataset.autocompleteValidate)) {
    opts["validate"] = !!parseInt(field.dataset.autocompleteValidate);
  }
  if ("undefined" !== typeof (field.dataset.inputWrapperSelector) && field.dataset.inputWrapperSelector.length) {
    opts["inputWrapperSelector"] = field.dataset.inputWrapperSelector;
  }
  if ("undefined" !== typeof (field.dataset.dataListSelector) && field.dataset.dataListSelector.length) {
    opts["dataListSelector"] = field.dataset.dataListSelector;
  }
  if (opts.validate) {
    // This causes a validation event to fire when the user clicks an autocomplete option
    /*connectMutationObserver(field, e => {
      if (!field.dataset.listLoaded) {
        return;
      }
      validateField(field, options.validateOptions ? options.validateOptions : options);
    });*/
    field.addEventListener("autocomplete.option.selected", () => {
      if (!field.dataset.listLoaded) {
        return;
      }
      validateField(field, options.validateOptions ? options.validateOptions : options);
    });
  }

  // Set configuration to use later
  field.dataset.inputWrapperSelector = opts.inputWrapperSelector;
  field.dataset.dataListSelector = opts.dataListSelector;

  // Save data list instance reference
  field.dataList = field.parentNode.querySelector(field.dataset.dataListSelector);

  // Add listeners with debounce
  field.disabled = true;
  const onListLoaded = list => {
    field.dataList.innerHTML = "";
    list.forEach(l => {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = templateAutocompleteOptions
        .replace("{%name%}", l.name)
        .replace("{%value%}", l.value);
      // Attach listener for click (selection)
      wrapper.firstElementChild.addEventListener(
        "click",
        debounce(
          () => {
            // Show selection in field (input) and select value
            field.value = l.inField;
            field.dataset.value = l.value;

            const event = new Event("autocomplete.option.selected");
            field.dispatchEvent(event);
          },
          100,
        ),
      );

      // Add options to data list
      field.dataList.appendChild(wrapper.firstElementChild);
    });
    field.dataset.listLoaded = "1";
    field.disabled = false;
  };
  if (opts.list && "function" === typeof (opts.list)) {
    field.addEventListener(
      "input",
      debounce(
        e => {
          const target = getEventTarget(e);
          delete target.dataset.listLoaded;
          opts.list(target.value, target).then(onListLoaded.bind());
        },
        300,
      ),
    );
  }
  if (opts.staticList && "function" === typeof (opts.staticList)) {
    opts.staticList(field.value, field).then(onListLoaded);
  }
  field.addEventListener("focus", () => {
    field.dataset.enteredField = "1";
  });
  field.addEventListener("focus", debounce(showDataList, 100));
  field.addEventListener("blur", debounce(hideDataList, 100));
  // Mark as initialized
  field.dataset.autocompleteInitialized = "1";
};

/**
 * Show autocomplete data list
 *
 * @param e {Event|HTMLElement}
 */
const showDataList = e => {
  const target = e.id ? e : e.target;
  const parent = getParentNodeWithClass(
    target,
    target.dataset.inputWrapperSelector ? target.dataset.inputWrapperSelector : defaultOptions.inputWrapperSelector,
  );
  const dataList = target.dataList ?
    target.dataList :
    parent.querySelector(
      target.dataset.dataListSelector ?
        target.dataset.dataListSelector
        :
        defaultOptions.dataListSelector,
    );
  if (dataList.childNodes.length) {
    parent.classList.add("open");
  }
};

/**
 * Hide autocomplete data list
 *
 * @param e {Event|HTMLElement}
 */
const hideDataList = e => {
  const target = e.id ? e : e.target;
  const parent = getParentNodeWithClass(
    target,
    target.dataset.inputWrapperSelector ? target.dataset.inputWrapperSelector : defaultOptions.inputWrapperSelector,
  );
  const dataList = target.dataList ?
    target.dataList :
    parent.querySelector(
      target.dataset.dataListSelector ? target.dataset.dataListSelector : defaultOptions.dataListSelector);
  if (dataList.childNodes.length) {
    parent.classList.remove("open");
  }
};

export {initAutocompleteFieldsInForm, initAutocompleteField, showDataList, hideDataList};
export default initAutocompleteFieldsInForm;
